import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const SecuritySection = ({ securityData, onUpdate }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(securityData?.twoFactorEnabled);
  const [showQRCode, setShowQRCode] = useState(false);

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordUpdate = () => {
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    alert('Password updated successfully');
    setShowPasswordForm(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const handleTwoFactorToggle = () => {
    if (!twoFactorEnabled) {
      setShowQRCode(true);
    } else {
      setTwoFactorEnabled(false);
      onUpdate({ twoFactorEnabled: false });
    }
  };

  const handleTwoFactorSetup = () => {
    setTwoFactorEnabled(true);
    setShowQRCode(false);
    onUpdate({ twoFactorEnabled: true });
    alert('Two-factor authentication enabled successfully');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">Security Settings</h2>
        <p className="text-sm text-muted-foreground">
          Manage your password and authentication methods
        </p>
      </div>
      <div className="clinical-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Password</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Last changed: {securityData?.lastPasswordChange}
            </p>
          </div>
          {!showPasswordForm && (
            <Button
              variant="outline"
              iconName="Key"
              iconPosition="left"
              onClick={() => setShowPasswordForm(true)}
            >
              Change Password
            </Button>
          )}
        </div>

        {showPasswordForm && (
          <div className="space-y-4 p-4 bg-muted rounded-lg">
            <Input
              label="Current Password"
              type="password"
              value={passwordData?.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e?.target?.value)}
              required
            />
            <Input
              label="New Password"
              type="password"
              value={passwordData?.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e?.target?.value)}
              description="Must be at least 8 characters with uppercase, lowercase, and numbers"
              required
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={passwordData?.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e?.target?.value)}
              required
            />
            <div className="flex gap-2 pt-2">
              <Button
                variant="default"
                onClick={handlePasswordUpdate}
              >
                Update Password
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Shield" size={20} color="var(--color-primary)" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-foreground">Password Requirements</h4>
              <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={12} color="var(--color-success)" />
                  Minimum 8 characters
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={12} color="var(--color-success)" />
                  At least one uppercase letter
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={12} color="var(--color-success)" />
                  At least one lowercase letter
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Check" size={12} color="var(--color-success)" />
                  At least one number
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="clinical-card p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <Icon name="Smartphone" size={24} color="var(--color-primary)" />
            <div>
              <h3 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add an extra layer of security to your account
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {twoFactorEnabled && (
              <span className="status-badge status-badge-success">
                <Icon name="CheckCircle2" size={14} />
                <span className="ml-1">Enabled</span>
              </span>
            )}
            <Checkbox
              checked={twoFactorEnabled}
              onChange={handleTwoFactorToggle}
            />
          </div>
        </div>

        {showQRCode && (
          <div className="p-4 bg-muted rounded-lg">
            <h4 className="text-sm font-semibold text-foreground mb-3">Setup Two-Factor Authentication</h4>
            <div className="flex flex-col items-center gap-4">
              <div className="w-48 h-48 bg-white p-4 rounded-lg">
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <Icon name="QrCode" size={64} color="var(--color-muted-foreground)" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center max-w-md">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
              <Input
                label="Verification Code"
                type="text"
                placeholder="Enter 6-digit code"
                className="max-w-xs"
              />
              <div className="flex gap-2">
                <Button
                  variant="default"
                  onClick={handleTwoFactorSetup}
                >
                  Verify & Enable
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowQRCode(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {twoFactorEnabled && !showQRCode && (
          <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
              <div>
                <p className="text-sm font-medium text-success">Two-factor authentication is active</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your account is protected with an additional security layer
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="clinical-card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Active Sessions</h3>
        <div className="space-y-3">
          {(Array.isArray(securityData?.activeSessions) ? securityData?.activeSessions : [])?.map((session) => (
            <div key={session?.id} className="flex items-start justify-between p-4 bg-muted rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name={session?.device === 'Desktop' ? 'Monitor' : 'Smartphone'} size={20} color="var(--color-primary)" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground">{session?.device}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{session?.location}</p>
                  <p className="text-xs text-muted-foreground">Last active: {session?.lastActive}</p>
                </div>
              </div>
              {session?.current ? (
                <span className="status-badge status-badge-success">Current</span>
              ) : (
                <Button variant="ghost" size="sm" iconName="LogOut">
                  End Session
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SecuritySection;